/**
 * Signals v2 — buckets with 1..N aspects.
 *
 * Each bucket is a logical "Signal" the user reasons about (e.g. "Location").
 * Aspects are the discrete sub-rules under a bucket (e.g. "High Tax",
 * "Low-Tax Src"). Most buckets have a single aspect; the data model
 * supports more without further migrations.
 *
 * Field roles:
 * - `name`: short badge label (target ≤ 16 chars). Doubles as the
 *   at-a-glance phrase in the contact-detail tile grid.
 * - `description` (per-aspect): the financial complexity / problem.
 *   Explains the financial mechanism and what's at stake.
 * - `psychological_strategic_angle` (per-aspect): how the advisor should
 *   position the message — tone, timing, framing guidance. Used at AI
 *   message-generation time to steer the LLM's voice and approach.
 */

export type SignalAspectDefault = {
  key: string;
  name: string;
  description: string;
  psychological_strategic_angle: string;
  /** Code-style pseudocode of the detection rule. Mirrors the calculator
   *  function in `pg.contactSignal.defaults.ts` one-for-one — same regex
   *  literals, same numeric thresholds, same field names — and is rendered
   *  monospaced on the Signals settings page so superadmins can grep the
   *  named regex constants and helpers in the source. */
  formula: string;
};

export type SignalBucketDefault = {
  key: string;
  name: string;
  aspects: readonly SignalAspectDefault[];
};

export const DEFAULT_SIGNAL_BUCKETS = [
  {
    key: 'retired',
    name: 'Retire Tax',
    aspects: [
      {
        key: 'retired_recent',
        name: 'Retire Tax',
        description:
          "The transition from accumulating assets to drawing them down is the single most consequential financial shift in a person's life. The order in which accounts are drawn, Social Security timing, and Roth conversions meaningfully affect after-tax income. Most people make these decisions by default.",
        psychological_strategic_angle:
          'Frame the transition as an educational complexity. The tone should be warm, direct, and peer-level. Do not use loss-framing or phrases like "before it\'s too late," as the prospect has already retired.',
        formula: [
          'firstFieldMatch(primary_title || primary_headline, /\\bretired\\b/i)',
          '  && (',
          '    experiences.length === 0',
          '    || monthsSinceRoleEnded(experiences[0]) <= 3',
          '    || (/\\bretired\\b/i.test(experiences[0].title)',
          '        && monthsSinceRoleStarted(experiences[0]) <= 3)',
          '  )',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'employment_gap',
    name: 'Roth Window',
    aspects: [
      {
        key: 'gap_60_plus',
        name: 'Roth Window',
        description:
          'A gap between employment and income restart is one of the only predictable windows where taxable income temporarily drops. This opens Roth conversion opportunities and capital gains harvesting that are unavailable when income is normal.',
        psychological_strategic_angle:
          'Present this as a general observation rather than commenting on their specific employment status, avoiding surveillance language. Frame it educationally as "things worth doing" when income drops.',
        formula: [
          'experiences.length > 0',
          '  && !hasCurrentRole(experiences)',
          '  && min_age >= 60',
          '  && monthsSinceRoleEnded(mostRecentPriorRole(experiences)) <= 1',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'job_change_rollover',
    name: '401k Roll',
    aspects: [
      {
        key: 'voluntary_change',
        name: '401k Roll',
        description:
          'A 401(k) left at a prior employer is subject to the investment options and fee structures of that plan, which the former employee no longer controls. A rollover is the only moment to restructure those investments without tax consequences.',
        psychological_strategic_angle:
          'Describe the decision point (leave it, roll it, or move it) without making a specific recommendation. Keep the call to action soft, like offering a "20-minute conversation".',
        formula: [
          'const current = experiences.find(rolePresent)',
          'const prior   = mostRecentPriorRole(experiences)',
          '',
          'current != null',
          '  && prior != null',
          '  && normalizeCompany(prior.company) !== normalizeCompany(current.company)',
          '  && monthsSinceRoleEnded(prior) <= 6',
          '  && roleTenureMonths(prior) >= 12',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'rsu_cliff',
    name: 'RSU Cliff',
    aspects: [
      {
        key: 'cliff_42_50_mo',
        name: 'RSU Cliff',
        description:
          'RSU vesting creates a tax liability on the vesting date regardless of whether shares are sold. Decisions regarding tax lot management and diversification must be made before or at the moment of vest, not after.',
        psychological_strategic_angle:
          'Reach out 8-10 months early to build trust before the decision. Tone should be matter-of-fact and peer-level. Strictly avoid anxiety-inducing language like "leaving money on the table".',
        formula: [
          'primary_org_is_rsu_granting',
          '  && rolePresent(experiences[0])',
          '  && 42 <= continuousTenureMonthsAtCurrentEmployer(experiences) <= 50',
          '  && SENIOR_IC_OR_ABOVE_TITLE_RE.test(experiences[0].title || primary_title)',
          '',
          '// SENIOR_IC_OR_ABOVE_TITLE_RE =',
          '//   /\\b(senior|staff|principal|distinguished|lead|head\\s+of|manager',
          '//     |director|vp|svp|evp|vice\\s+president|president|chief|ceo|cto',
          '//     |cfo|coo|cmo|cso|cio|chro|founder|co-?founder|partner)\\b/i',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'long_tenure',
    name: 'NUA Tenure',
    aspects: [
      {
        key: 'tenure_18_22_yr',
        name: 'NUA Tenure',
        description:
          "Long tenure typically means significant 401(k) accumulation in that employer's plan, often with unchecked company stock concentration. These employees are prime candidates for Net Unrealized Appreciation (NUA) tax treatment.",
        psychological_strategic_angle:
          'This is a "nurture-to-watch" signal. Use an unhurried, collegial approach. Focus on general complexity without explicitly referencing NUA, as NUA eligibility requires discovery.',
        formula: [
          '(primary_org_is_rsu_granting || primary_org_is_equity_granting)',
          '  && hasCurrentRole(experiences)',
          '  && 18 <= continuousTenureMonthsAtCurrentEmployer(experiences) / 12 <= 22',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'age_proxy_63_64',
    name: 'IRMAA 63-64',
    aspects: [
      {
        key: 'age_63_64',
        name: 'IRMAA 63-64',
        description:
          'Medicare Part B and D premiums are calculated using IRMAA, which is based on income reported two years prior. A large income event now can trigger avoidable surcharges when Medicare begins.',
        psychological_strategic_angle:
          'Educate the prospect on the mechanical two-year lookback. Avoid quoting specific dollar surcharges, which implies the advisor has improperly assessed their unshared income.',
        formula: 'min_age === 63 || min_age === 64',
      },
    ],
  },
  {
    key: 'age_proxy_65',
    name: 'Medicare 65',
    aspects: [
      {
        key: 'age_65',
        name: 'Medicare 65',
        description:
          "Medicare's Initial Enrollment Period carries a statutory late enrollment penalty of 10% added to the Part B premium for every 12-month delay. Social Security filing timing has permanently compounding effects.",
        psychological_strategic_angle:
          'Use the hard statutory deadline to create natural urgency without manufactured alarm. Invite a conversation to check timelines without recommending a specific filing date.',
        formula: 'min_age >= 65',
      },
    ],
  },
  {
    key: 'senior_promotion',
    name: 'NQDC Promo',
    aspects: [
      {
        key: 'promo_internal',
        name: 'NQDC Promo',
        description:
          'A promotion to VP or C-suite typically triggers expanded equity grants and possible access to nonqualified deferred compensation (NQDC). NQDC enrollment elections must be made before the compensation is earned and cannot be retroactively modified.',
        psychological_strategic_angle:
          'Discuss the general complexity of executive compensation without assuming they have a specific NQDC plan. Invite planning to ensure their infrastructure keeps pace with the new role.',
        formula: [
          'const current = experiences.find(rolePresent)',
          'const priorSameCompany = experiences',
          '  .filter(e => !rolePresent(e))',
          '  .find(e => normalizeCompany(e.company) === normalizeCompany(current.company))',
          '',
          'classifyTitleTier(current.title || primary_title) ∈ {vp, csuite}',
          '  && priorSameCompany != null',
          '  && TIER_RANK[classifyTitleTier(priorSameCompany.title)] < TIER_RANK.vp',
          '  && roleTenureMonths(current) <= 18',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'location_modifier',
    name: 'Tax State',
    aspects: [
      {
        key: 'high_tax_resident',
        name: 'High Tax',
        description:
          'State income tax meaningfully affects the after-tax value of every income, equity, and distribution decision. The marginal rate in California reaches 13.3%, and 10.9% in New York.',
        psychological_strategic_angle:
          'Position the advisor as someone who plans for both federal and state tax layers. The message describes a real planning dimension without claiming specific savings.',
        formula: [
          'matchHighTaxLocation((primary_city_full || primary_city).toLowerCase()) != null',
          '',
          '// Matches an unambiguous high-tax STATE (CA, NY, NJ, OR, MN, HI, DC, CT, ME, IA),',
          '// OR a high-tax CITY when the haystack ALSO contains the matching state qualifier.',
          '// This avoids false positives like "Oakland, Michigan" matching as CA.',
          '//',
          '// HIGH_TAX_STATE_RE = /\\b(california|new york(?! mills)|new jersey|oregon',
          '//   |minnesota|hawaii|washington[,\\s]*d\\.?c\\.?|district\\s+of\\s+columbia',
          '//   |connecticut|maine|iowa)\\b/',
          '//',
          '// City + required state qualifier pairs:',
          '//   CA cities (san francisco, los angeles, san diego, san jose, palo alto,',
          '//              mountain view, oakland, sacramento) + (ca|calif|california)',
          '//   NY cities (nyc, new york city, manhattan, brooklyn, queens, bronx) + (ny|new york)',
          '//   NJ cities (jersey city, newark, hoboken) + (nj|new jersey)',
          '//   portland + (or|ore|oregon|me|maine)',
          '//   MN cities (minneapolis, saint paul, st. paul) + (mn|minn|minnesota)',
          '//   honolulu + (hi|hawaii)',
          '//   CT cities (hartford, stamford, new haven, bridgeport) + (ct|conn|connecticut)',
          '//   des moines + (ia|iowa)',
        ].join('\n'),
      },
      {
        key: 'low_tax_sourcing',
        name: 'Low-Tax Src',
        description:
          'Deferred compensation earned or RSUs vested while a resident of a high-tax state may still be subject to source-state taxation after the individual moves. This is a common and underappreciated exposure.',
        psychological_strategic_angle:
          'Frame strictly as a conditional observation (e.g., "depending on your history") rather than a definitive finding to avoid implying a fake tax liability. It requires compliance counsel review.',
        formula: [
          'isLowTaxLocation((primary_city_full || primary_city).toLowerCase())',
          '  && ∃ h ∈ historical_orgs where',
          '       h.hq_state ∈ {"CA", "NY"}',
          '    && !rolePresent(h)',
          '    && roleTenureMonths(h) >= 36',
          '    && (',
          '         monthsSinceRoleEnded(h) <= 48',
          '      || classifyTitleTier(h.title) ∈ {vp, csuite}',
          '       )',
          '',
          '// isLowTaxLocation = LOW_TAX_LOCATION_RE.test(s) || WASHINGTON_STATE_RE.test(s)',
          '// LOW_TAX_LOCATION_RE = /\\b(texas|florida|nevada|tennessee|wyoming',
          '//   |south\\s+dakota|alaska|austin|dallas|houston|san\\s+antonio',
          '//   |fort\\s+worth|miami|tampa|orlando|jacksonville|tallahassee',
          '//   |las\\s+vegas|reno|nashville|memphis|knoxville|chattanooga',
          '//   |cheyenne|sioux\\s+falls|anchorage|seattle|bellevue|redmond',
          '//   |tacoma|spokane)\\b/',
          '// WASHINGTON_STATE_RE = /\\bwashington\\b(?!\\s*[,]?\\s*d\\.?c\\.?)/i',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'golden_handcuffs_457f',
    name: '457(f) Cliff',
    aspects: [
      {
        key: 'cliff_457f',
        name: '457(f) Cliff',
        description:
          'Under IRS Code 457(f), tax-exempt organizations offer deferred compensation subject to a "substantial risk of forfeiture." These contracts typically vest on strict 3, 5, or 7-year cliffs. The exact moment the cliff is reached, the forfeiture risk vanishes and the entire accumulated balance is instantly taxed as ordinary income for that year, potentially creating a catastrophic cash-flow crisis.',
        psychological_strategic_angle:
          'Frame outreach around "navigating complex, executive-level compensation structures unique to higher education/non-profits." Do not assume they have a 457(f); present it as a structural planning milestone.',
        formula: [
          'const m = continuousTenureMonthsAtCurrentEmployer(experiences)',
          '',
          'rolePresent(experiences[0])',
          '  && NONPROFIT_LEADER_RE.test(experiences[0].title || primary_title)',
          '  && NONPROFIT_EMPLOYER_RE.test(experiences[0].company)',
          '  && (33 <= m <= 36 || 57 <= m <= 60 || 81 <= m <= 84)',
          '',
          '// NONPROFIT_LEADER_RE   = /\\b(dean|provost|president|chief)\\b/i',
          '// NONPROFIT_EMPLOYER_RE = /\\b(university|college|hospital|foundation|school\\s+of)\\b/i',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'secure2_catchup',
    name: 'Catch-up',
    aspects: [
      {
        key: 'catchup_collision',
        name: 'Catch-up',
        description:
          'SECURE 2.0 allows execs aged 60-63 a "super" 401(k) catch-up limit. However, anyone earning over the threshold (~$145K) must make catch-ups as after-tax Roth contributions, abruptly losing the upfront pre-tax deduction, reducing net paycheck, and forcing a recalculation of current-year tax liability strategy.',
        psychological_strategic_angle:
          'Be highly educational. Frame it as "optimizing the new SECURE 2.0 rules" rather than focusing on the loss of a deduction. Keep it peer-level, analytical, and focused on tax efficiency.',
        formula: [
          '(min_age === 50 || 60 <= min_age <= 63)',
          '  && EXEC_TITLE_RE.test(primary_title || experiences[0].title)',
          '',
          '// EXEC_TITLE_RE = /\\b(svp|evp|vp|vice\\s+president|senior\\s+vice\\s+president',
          '//   |executive\\s+vice\\s+president|ceo|cto|cfo|coo|cmo|cso|cio|chro|chief',
          '//   |president|founder|co-?founder|director|managing\\s+director',
          '//   |senior\\s+director|executive\\s+director)\\b/i',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'state_relocation',
    name: 'Relocation',
    aspects: [
      {
        key: 'high_to_low_recent',
        name: 'Relocation',
        description:
          'States like CA and NY enforce "source-state taxation." Unvested RSUs or deferred comp earned while living there are taxed by that state, regardless of where the employee lives now. Executives who move to zero-tax states assuming their tax burden is gone can trigger domicile audits or double-taxation surprises when exercising options or drawing comp.',
        psychological_strategic_angle:
          'Position as an educational heads-up about hidden cross-border tax exposures. Use qualifiers like "depending on your employment history" to avoid guaranteeing a tax liability.',
        formula: [
          'rolePresent(experiences[0])',
          '  && monthsSinceRoleStarted(experiences[0]) <= 3',
          '  && isLowTaxLocation(',
          '       (experiences[0].location || primary_city_full || primary_city).toLowerCase()',
          '     )',
          '  && matchHighTaxLocation(experiences[1].location.toLowerCase()) != null',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'sandwich_gen',
    name: 'Sandwich Gen',
    aspects: [
      {
        key: 'age_45_52_exec',
        name: 'Sandwich Gen',
        description:
          'Biologically and structurally, ages 45-52 collide peak earning years with peak capital outflows: children hitting college age and parents entering eldercare. This is the exact chronological window where basic wills fail and complex estate/trust structures become mandatory to protect wealth while funding dual generations.',
        psychological_strategic_angle:
          'Speak to "the complexity of managing competing financial priorities at the executive tier." Completely avoid assuming they have children or sick parents. Speak purely to foundational structural protection.',
        formula: [
          '45 <= min_age <= 52',
          '  && EXEC_TITLE_RE.test(primary_title || experiences[0].title)',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'founder_liquidity',
    name: 'Founder Wall',
    aspects: [
      {
        key: 'tenure_7_10_yr',
        name: 'Founder Wall',
        description:
          'Small and mid-market business growth typically hits a structural ceiling around the 10-year mark, forcing founders to scale via acquisition, sell, or step back. The founder\'s net worth is entirely illiquid and trapped — if they wait until a Letter of Intent (LOI) to execute tax-saving estate strategies (like GRATs), it is legally too late.',
        psychological_strategic_angle:
          'Frame the conversation around the "typical evolution of a 10-year-old business" and the "shift from building enterprise value to protecting personal wealth." Do not predict their specific exit.',
        formula: [
          'rolePresent(experiences[0])',
          '  && FOUNDER_OWNER_RE.test(experiences[0].title || primary_title)',
          '  && 84 <= continuousTenureMonthsAtCurrentEmployer(experiences) <= 120',
          '',
          '// FOUNDER_OWNER_RE = /\\b(founder|co-?founder|owner|co-?owner|managing\\s+partner)\\b/i',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'rmd_cliff',
    name: 'RMD 73 Cliff',
    aspects: [
      {
        key: 'age_71_72',
        name: 'RMD 73 Cliff',
        description:
          'The IRS mandates that individuals must begin taking Required Minimum Distributions (RMDs) from tax-deferred retirement accounts at age 73. At that point the government forces taxable income, permanently altering the tax baseline and triggering IRMAA surcharges. Ages 71/72 are the final window for Roth conversions or QCDs.',
        psychological_strategic_angle:
          'Compliantly invite a conversation about "planning for the age 73 tax shift." Do not project their specific tax bracket or Medicare penalty exposure. Root the urgency in statutory deadlines.',
        formula: 'min_age === 71 || min_age === 72',
      },
    ],
  },
  {
    key: 'family_formation',
    name: 'Family Form',
    aspects: [
      {
        key: 'parent_signal',
        name: 'Family Form',
        description:
          'Adding a dependent instantly creates a multi-million-dollar unfunded liability (housing expansion, 18 years of living costs, college tuition). DINKs can survive lazy planning; parents cannot. This triggers an immediate, structural need for term life insurance, guardianship designations, and 529s.',
        psychological_strategic_angle:
          'STRICTLY NO SURVEILLANCE. Never use the words "kid," "child," or "leave." Frame the outreach entirely around their "career stage" and the shift from accumulating wealth to securing it for the long term.',
        formula: [
          'PARENTAL_HEADLINE_RE.test(primary_headline)',
          '  || (',
          '    const gap = monthsSinceRoleEnded(experiences[1])',
          '              - monthsSinceRoleStarted(experiences[0])',
          '    1 <= gap <= 6 && PARENTAL_LEAVE_RE.test(experiences[1].title)',
          '  )',
          '',
          '// PARENTAL_HEADLINE_RE = /(?:^|[^a-z])(mom|dad|father|mother|parent)(?:[^a-z]|$)/i',
          '// PARENTAL_LEAVE_RE    = /\\b(parental|maternity|paternity|family\\s+leave)\\b/i',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'm_a_collision',
    name: 'M&A',
    aspects: [
      {
        key: 'acquired_by',
        name: 'M&A',
        description:
          'Corporate acquisitions trigger specific legal clauses like single/double-trigger equity acceleration, and complex earn-out structures mandated by the buyer. The executive faces a violently compressed tax window — unvested equity vesting all at once creates a massive tax spike, requiring immediate decisions on retention bonuses and stock options.',
        psychological_strategic_angle:
          'Do not use words like "windfall" or "payout." Focus entirely on the complexity of integrating two compensation structures and optimizing their personal tax picture over the next 24 months.',
        formula: [
          'ACQUIRED_BY_RE.test(primary_headline)',
          '  && (',
          '    EXEC_TITLE_RE.test(primary_title || experiences[0].title)',
          '    || FOUNDER_OWNER_RE.test(primary_title || experiences[0].title)',
          '  )',
          '',
          '// ACQUIRED_BY_RE = /\\bacquired\\s+by\\b/i',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'partner_track',
    name: 'Partner Track',
    aspects: [
      {
        key: 'new_partner_md',
        name: 'Partner Track',
        description:
          'Making partner shifts employment status from W-2 employee to Schedule K-1 business owner. It stops automatic employer tax withholding and requires a capital buy-in. The new partner faces a massive cash drain to buy into the partnership, and they suddenly have to manage quarterly estimated taxes and self-structured benefits.',
        psychological_strategic_angle:
          'Tone must be congratulatory but grounded. Pivot quickly from the milestone to the structural efficiency needs of the W-2 to K-1 transition. Do not assume they are struggling to afford the buy-in.',
        formula: [
          'rolePresent(experiences[0])',
          '  && PARTNER_MD_RE.test(experiences[0].title)',
          '  && monthsSinceRoleStarted(experiences[0]) <= 3',
          '  && normalizeCompany(experiences[1].company)',
          '       === normalizeCompany(experiences[0].company)',
          '  && !PARTNER_MD_RE.test(experiences[1].title)',
          '',
          '// PARTNER_MD_RE = /\\b(partner|managing\\s+director)\\b/i',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'portfolio_career',
    name: 'Portfolio',
    aspects: [
      {
        key: 'fractional_2plus',
        name: 'Portfolio',
        description:
          'Independent contractors (1099 workers) lack corporate 401(k) plans but can open self-employed retirement accounts (Solo 401(k), SEP IRA) with significantly higher contribution limits. Compensation is highly fragmented and largely illiquid (advisory shares); they must aggressively build their own tax-sheltered retirement infrastructure or lose massive consulting income to taxes.',
        psychological_strategic_angle:
          'Avoid the word "retired," as many executives view advisory work as peak active earning. Use terms like "portfolio career" or "advisory chapter." Emphasize centralizing fragmented equity.',
        formula: [
          'experiences',
          '  .filter(rolePresent)',
          '  .filter(e => FRACTIONAL_RE.test(e.title))',
          '  .length >= 2',
          '  && ∃ prior ∈ experiences where',
          '       !rolePresent(prior)',
          '    && classifyTitleTier(prior.title) ∈ {vp, csuite}',
          '',
          '// FRACTIONAL_RE = /\\b(board\\s+member|advisor|fractional|investor)\\b/i',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'boomerang',
    name: 'Boomerang',
    aspects: [
      {
        key: 'post_sabbatical',
        name: 'Boomerang',
        description:
          'Taking a multi-year gap means forfeiting years of tax-advantaged retirement space, while likely drawing heavily on taxable cash reserves. Re-entering at a high earning level requires an immediate, aggressive cash-flow model to rebuild depleted liquidity buffers, maximize newly available tax space, and optimize new-hire equity.',
        psychological_strategic_angle:
          'Frame the gap positively as a "strategic career break." Focus entirely on momentum, rebuilding, and "catch-up architecture." Never inquire about the personal reasons for the gap.',
        formula: [
          'const gap = monthsSinceRoleEnded(experiences[1])',
          '          - monthsSinceRoleStarted(experiences[0])',
          '',
          'rolePresent(experiences[0])',
          '  && monthsSinceRoleStarted(experiences[0]) <= 3',
          '  && 12 <= gap <= 24',
          '  && EXEC_TITLE_RE.test(experiences[0].title || primary_title)',
        ].join('\n'),
      },
    ],
  },
  {
    key: 'expat_repat',
    name: 'Expat',
    aspects: [
      {
        key: 'cross_border_recent',
        name: 'Expat',
        description:
          'The US taxes global income. Expats face FEIE, FBAR reporting, and catastrophic PFIC penalties for foreign mutual funds. Repatriating can trigger foreign exit taxes. Standard domestic financial advice is actively toxic for expats — moving across borders without specialized tax architecture guarantees double-taxation and massive reporting penalties.',
        psychological_strategic_angle:
          'Tone should be highly specialized and authoritative. Emphasize an "architectural approach" over product selling. Note the complexity without using fear tactics or threatening IRS audits.',
        formula: [
          'rolePresent(experiences[0])',
          '  && monthsSinceRoleStarted(experiences[0]) <= 4',
          '  && looksUSLocation(experiences[0].location) != null',
          '  && looksUSLocation(experiences[1].location) != null',
          '  && looksUSLocation(experiences[0].location)',
          '       !== looksUSLocation(experiences[1].location)',
          '',
          '// looksUSLocation(s) = US_LOCATION_RE.test(s.trim())',
          '// US_LOCATION_RE matches /\\b(united\\s+states|usa|u\\.?s\\.?a?\\.?',
          '//   |<all 50 state names>|district\\s+of\\s+columbia|d\\.?c\\.?)\\b/i',
        ].join('\n'),
      },
    ],
  },
] as const satisfies readonly SignalBucketDefault[];

export type DefaultSignalBucketKey = (typeof DEFAULT_SIGNAL_BUCKETS)[number]['key'];

export type DefaultSignalAspectKey =
  (typeof DEFAULT_SIGNAL_BUCKETS)[number]['aspects'][number]['key'];

export const DEFAULT_SIGNAL_BUCKET_KEYS = DEFAULT_SIGNAL_BUCKETS.map((b) => b.key) as readonly DefaultSignalBucketKey[];

export const DEFAULT_SIGNAL_ASPECT_KEYS: readonly DefaultSignalAspectKey[] = DEFAULT_SIGNAL_BUCKETS.flatMap(
  (b) => b.aspects.map((a) => a.key),
) as readonly DefaultSignalAspectKey[];
